import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Plus, Pencil, Trash2, Banknote, Landmark, Smartphone, Wallet,
  ArrowUpCircle, ArrowDownCircle, CreditCard, DollarSign, Receipt,
  ArrowLeft, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[module]?.[action] ?? false;
};

function TreasuryConfig() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cash-banks');
  const [loading, setLoading] = useState(true);

  const [cashBanks, setCashBanks] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);
  const [incomeConcepts, setIncomeConcepts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [withholdingTypes, setWithholdingTypes] = useState([]);
  const [mappings, setMappings] = useState({});
  const [accountNames, setAccountNames] = useState({});

  const [cashBankDialogOpen, setCashBankDialogOpen] = useState(false);
  const [expenseConceptDialogOpen, setExpenseConceptDialogOpen] = useState(false);
  const [incomeConceptDialogOpen, setIncomeConceptDialogOpen] = useState(false);
  const [paymentTypeDialogOpen, setPaymentTypeDialogOpen] = useState(false);
  const [withholdingDialogOpen, setWithholdingDialogOpen] = useState(false);

  const [editingCashBank, setEditingCashBank] = useState(null);
  const [editingExpenseConcept, setEditingExpenseConcept] = useState(null);
  const [editingIncomeConcept, setEditingIncomeConcept] = useState(null);
  const [editingPaymentType, setEditingPaymentType] = useState(null);
  const [editingWithholding, setEditingWithholding] = useState(null);

  const [cashBankForm, setCashBankForm] = useState({
    name: '', currency: 'ARS', entity_type: 'cash', account_number: '', cbu_alias: '',
    initial_balance: 0, initial_balance_date: new Date().toISOString().split('T')[0], allow_negative: false,
    allowed_payment_categories: null,  // v3.36.0 — null = sin restricciones
  });
  const [expenseConceptForm, setExpenseConceptForm] = useState({ name: '', behavior: 'debit' });
  const [incomeConceptForm, setIncomeConceptForm] = useState({ name: '', behavior: 'credit' });
  const [paymentTypeForm, setPaymentTypeForm] = useState({ name: '', category: 'cash', requires_reference: false });
  const [withholdingForm, setWithholdingForm] = useState({ code: '', name: '' });

  const canCreate = userCan(user, 'tesoreria_config', 'crear');
  const canEdit = userCan(user, 'tesoreria_config', 'editar');
  const canDelete = userCan(user, 'tesoreria_config', 'eliminar');

  // Devuelve el badge de la cuenta contable mapeada para un concepto (vía account_mappings)
  const renderMappedAccount = (entityType, entityId) => {
    const code = mappings[`${entityType}::${entityId}`];
    if (!code) return <span className="text-xs text-muted-foreground">Sin mapear</span>;
    const name = accountNames[code];
    return (
      <Badge variant="outline" className="gap-1 font-normal" data-testid="mapped-account-badge">
        <Landmark className="w-3 h-3" />
        <span className="font-mono">{code}</span>{name ? ` · ${name}` : ''}
      </Badge>
    );
  };

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    try {
      const [cbRes, ecRes, icRes, ptRes, mapRes, accRes, whRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/treasury/cash-banks`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/expense-concepts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/income-concepts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/payment-types`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/accounting/mappings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/accounting/accounts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/treasury/withholding-types`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCashBanks(cbRes.data);
      setExpenseConcepts(ecRes.data);
      setIncomeConcepts(icRes.data);
      setPaymentTypes(ptRes.data);
      setWithholdingTypes(whRes.data || []);
      const m = {};
      (mapRes.data || []).forEach(x => { m[`${x.entity_type}::${x.entity_id}`] = x.account_code; });
      setMappings(m);
      const names = {};
      (accRes.data || []).forEach(a => { names[a.code] = a.name; });
      setAccountNames(names);
    } catch { toast.error('Error al cargar datos'); } finally { setLoading(false); }
  };

  // v3.36.0 — Defaults de categorías permitidas por entity_type (sugerencia inicial, el admin ajusta)
  const DEFAULT_CATS_BY_ENTITY_TYPE = {
    cash: ['cash'],
    bank: ['transfer', 'check', 'card'],
    credit_card: ['card'],
    wallet: ['cash', 'transfer'],
  };

  // ===== CAJAS/BANCOS =====
  const handleOpenCashBankCreate = () => {
    setEditingCashBank(null);
    setCashBankForm({ name: '', currency: 'ARS', entity_type: 'cash', account_number: '', cbu_alias: '', initial_balance: 0, initial_balance_date: new Date().toISOString().split('T')[0], allow_negative: false, allowed_payment_categories: DEFAULT_CATS_BY_ENTITY_TYPE['cash'] });
    setCashBankDialogOpen(true);
  };
  const handleOpenCashBankEdit = (cb) => {
    setEditingCashBank(cb);
    // v3.26.0 — precargar saldo inicial de la ficha (no el current_balance)
    setCashBankForm({
      name: cb.name, currency: cb.currency, entity_type: cb.entity_type,
      account_number: cb.account_number || '', cbu_alias: cb.cbu_alias || '',
      initial_balance: cb.initial_balance || 0,
      initial_balance_date: cb.initial_balance_date ? String(cb.initial_balance_date).slice(0, 10) : new Date().toISOString().split('T')[0],
      allow_negative: cb.allow_negative,
      allowed_payment_categories: cb.allowed_payment_categories ?? null,  // v3.36.0
    });
    setCashBankDialogOpen(true);
  };
  const handleSaveCashBank = async () => {
    if (!cashBankForm.name.trim()) { toast.error('Ingresa el nombre'); return; }
    try {
      if (editingCashBank) {
        await axios.put(`${BACKEND_URL}/api/treasury/cash-banks/${editingCashBank.cash_bank_id}`, {
          name: cashBankForm.name, currency: cashBankForm.currency, entity_type: cashBankForm.entity_type,
          account_number: cashBankForm.account_number || null, cbu_alias: cashBankForm.cbu_alias || null,
          allow_negative: cashBankForm.allow_negative,
          allowed_payment_categories: cashBankForm.allowed_payment_categories,  // v3.36.0
          // v3.26.0 — permitir corregir saldo inicial desde la edición
          initial_balance: cashBankForm.initial_balance ?? 0,
          initial_balance_date: cashBankForm.initial_balance_date || null,
        }, { withCredentials: true });
        toast.success('Caja/Banco actualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/treasury/cash-banks`, cashBankForm, { withCredentials: true });
        toast.success('Caja/Banco creado');
      }
      setCashBankDialogOpen(false);
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
  };
  const handleDeleteCashBank = async (cb) => {
    if (!window.confirm(`Eliminar "${cb.name}"?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/treasury/cash-banks/${cb.cash_bank_id}/delete`, {}, { withCredentials: true });
      toast.success('Eliminado'); fetchAllData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar';
      if (msg.includes('movimientos asociados')) {
        if (window.confirm(`${msg}\n\nDesea desactivar "${cb.name}" en su lugar?`)) handleToggleCashBankActive({ ...cb, is_active: true });
      } else toast.error(msg);
    }
  };
  const handleToggleCashBankActive = async (cb) => {
    try {
      await axios.put(`${BACKEND_URL}/api/treasury/cash-banks/${cb.cash_bank_id}`, { is_active: !cb.is_active }, { withCredentials: true });
      toast.success(cb.is_active ? 'Desactivado' : 'Activado'); fetchAllData();
    } catch { toast.error('Error al actualizar'); }
  };

  // ===== CONCEPTOS GASTOS =====
  const handleOpenExpenseConceptCreate = () => { setEditingExpenseConcept(null); setExpenseConceptForm({ name: '', behavior: 'debit' }); setExpenseConceptDialogOpen(true); };
  const handleOpenExpenseConceptEdit = (ec) => { setEditingExpenseConcept(ec); setExpenseConceptForm({ name: ec.name, behavior: ec.behavior }); setExpenseConceptDialogOpen(true); };
  const handleSaveExpenseConcept = async () => {
    if (!expenseConceptForm.name.trim()) { toast.error('Ingresa el nombre'); return; }
    try {
      if (editingExpenseConcept) { await axios.put(`${BACKEND_URL}/api/treasury/expense-concepts/${editingExpenseConcept.concept_id}`, expenseConceptForm, { withCredentials: true }); toast.success('Concepto actualizado'); }
      else { await axios.post(`${BACKEND_URL}/api/treasury/expense-concepts`, expenseConceptForm, { withCredentials: true }); toast.success('Concepto creado'); }
      setExpenseConceptDialogOpen(false); fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
  };
  const handleDeleteExpenseConcept = async (ec) => {
    if (!window.confirm(`Eliminar "${ec.name}"?`)) return;
    try { await axios.post(`${BACKEND_URL}/api/treasury/expense-concepts/${ec.concept_id}/delete`, {}, { withCredentials: true }); toast.success('Eliminado'); fetchAllData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Error al eliminar'); }
  };
  const handleToggleExpenseConceptActive = async (ec) => {
    try { await axios.put(`${BACKEND_URL}/api/treasury/expense-concepts/${ec.concept_id}`, { is_active: !ec.is_active }, { withCredentials: true }); toast.success(ec.is_active ? 'Desactivado' : 'Activado'); fetchAllData(); }
    catch { toast.error('Error al actualizar'); }
  };

  // ===== CONCEPTOS COBRANZAS =====
  const handleOpenIncomeConceptCreate = () => { setEditingIncomeConcept(null); setIncomeConceptForm({ name: '', behavior: 'credit' }); setIncomeConceptDialogOpen(true); };
  const handleOpenIncomeConceptEdit = (ic) => { setEditingIncomeConcept(ic); setIncomeConceptForm({ name: ic.name, behavior: ic.behavior }); setIncomeConceptDialogOpen(true); };
  const handleSaveIncomeConcept = async () => {
    if (!incomeConceptForm.name.trim()) { toast.error('Ingresa el nombre'); return; }
    try {
      if (editingIncomeConcept) { await axios.put(`${BACKEND_URL}/api/treasury/income-concepts/${editingIncomeConcept.concept_id}`, incomeConceptForm, { withCredentials: true }); toast.success('Concepto actualizado'); }
      else { await axios.post(`${BACKEND_URL}/api/treasury/income-concepts`, incomeConceptForm, { withCredentials: true }); toast.success('Concepto creado'); }
      setIncomeConceptDialogOpen(false); fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
  };
  const handleDeleteIncomeConcept = async (ic) => {
    if (!window.confirm(`Eliminar "${ic.name}"?`)) return;
    try { await axios.post(`${BACKEND_URL}/api/treasury/income-concepts/${ic.concept_id}/delete`, {}, { withCredentials: true }); toast.success('Eliminado'); fetchAllData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Error al eliminar'); }
  };
  const handleToggleIncomeConceptActive = async (ic) => {
    try { await axios.put(`${BACKEND_URL}/api/treasury/income-concepts/${ic.concept_id}`, { is_active: !ic.is_active }, { withCredentials: true }); toast.success(ic.is_active ? 'Desactivado' : 'Activado'); fetchAllData(); }
    catch { toast.error('Error al actualizar'); }
  };

  // ===== TIPOS DE VALORES =====
  const handleOpenPaymentTypeCreate = () => { setEditingPaymentType(null); setPaymentTypeForm({ name: '', category: 'cash', requires_reference: false }); setPaymentTypeDialogOpen(true); };
  const handleOpenPaymentTypeEdit = (pt) => { setEditingPaymentType(pt); setPaymentTypeForm({ name: pt.name, category: pt.category, requires_reference: pt.requires_reference }); setPaymentTypeDialogOpen(true); };
  const handleSavePaymentType = async () => {
    if (!paymentTypeForm.name.trim()) { toast.error('Ingresa el nombre'); return; }
    try {
      if (editingPaymentType) { await axios.put(`${BACKEND_URL}/api/treasury/payment-types/${editingPaymentType.payment_type_id}`, paymentTypeForm, { withCredentials: true }); toast.success('Tipo actualizado'); }
      else { await axios.post(`${BACKEND_URL}/api/treasury/payment-types`, paymentTypeForm, { withCredentials: true }); toast.success('Tipo creado'); }
      setPaymentTypeDialogOpen(false); fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
  };
  const handleDeletePaymentType = async (pt) => {
    if (!window.confirm(`Eliminar "${pt.name}"?`)) return;
    try { await axios.post(`${BACKEND_URL}/api/treasury/payment-types/${pt.payment_type_id}/delete`, {}, { withCredentials: true }); toast.success('Eliminado'); fetchAllData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Error al eliminar'); }
  };
  const handleTogglePaymentTypeActive = async (pt) => {
    try { await axios.put(`${BACKEND_URL}/api/treasury/payment-types/${pt.payment_type_id}`, { is_active: !pt.is_active }, { withCredentials: true }); toast.success(pt.is_active ? 'Desactivado' : 'Activado'); fetchAllData(); }
    catch { toast.error('Error al actualizar'); }
  };

  // ===== RETENCIONES / PERCEPCIONES =====
  const handleOpenWithholdingCreate = () => { setEditingWithholding(null); setWithholdingForm({ code: '', name: '' }); setWithholdingDialogOpen(true); };
  const handleOpenWithholdingEdit = (w) => { setEditingWithholding(w); setWithholdingForm({ code: w.code, name: w.name }); setWithholdingDialogOpen(true); };
  const handleSaveWithholding = async () => {
    if (!withholdingForm.code.trim() || !withholdingForm.name.trim()) { toast.error('Ingresá código y nombre'); return; }
    try {
      if (editingWithholding) { await axios.put(`${BACKEND_URL}/api/treasury/withholding-types/${editingWithholding.withholding_type_id}`, withholdingForm, { withCredentials: true }); toast.success('Retención actualizada'); }
      else { await axios.post(`${BACKEND_URL}/api/treasury/withholding-types`, withholdingForm, { withCredentials: true }); toast.success('Retención creada'); }
      setWithholdingDialogOpen(false); fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
  };
  const handleDeleteWithholding = async (w) => {
    if (!window.confirm(`Desactivar "${w.code} · ${w.name}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/treasury/withholding-types/${w.withholding_type_id}`, { withCredentials: true }); toast.success('Desactivada'); fetchAllData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Error al eliminar'); }
  };


  // Helpers
  const getEntityTypeIcon = (type) => {
    switch (type) { case 'cash': return <Banknote className="w-4 h-4" />; case 'bank': return <Landmark className="w-4 h-4" />; case 'wallet': return <Smartphone className="w-4 h-4" />; case 'credit_card': return <CreditCard className="w-4 h-4" />; default: return <Wallet className="w-4 h-4" />; }
  };
  const getEntityTypeName = (type) => {
    switch (type) { case 'cash': return 'Caja Fisica'; case 'bank': return 'Cuenta Bancaria'; case 'wallet': return 'Billetera Virtual'; case 'credit_card': return 'Tarjeta de Credito'; default: return type; }
  };
  const getCategoryIcon = (cat) => {
    switch (cat) { case 'cash': return <Banknote className="w-4 h-4" />; case 'transfer': return <ArrowUpCircle className="w-4 h-4" />; case 'check': return <Receipt className="w-4 h-4" />; case 'card': return <CreditCard className="w-4 h-4" />; default: return <DollarSign className="w-4 h-4" />; }
  };
  const getCategoryName = (cat) => {
    switch (cat) { case 'cash': return 'Efectivo'; case 'transfer': return 'Transferencia'; case 'check': return 'Cheque'; case 'card': return 'Tarjeta'; default: return cat; }
  };
  const formatCurrency = (amount, currency = 'ARS') =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);

  if (loading) return <div className="flex items-center justify-center h-64"><p>Cargando...</p></div>;

  return (
    <div className="space-y-6" data-testid="treasury-config-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/treasury')} data-testid="back-to-treasury">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Settings className="w-6 h-6" /> Configuracion de Tesoreria
            </h1>
            <p className="text-muted-foreground">Cajas, bancos, conceptos y valores</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="cash-banks" className="text-xs sm:text-sm" data-testid="tab-cash-banks">Cajas/Bancos</TabsTrigger>
          <TabsTrigger value="expenses" className="text-xs sm:text-sm" data-testid="tab-expenses">Gastos</TabsTrigger>
          <TabsTrigger value="incomes" className="text-xs sm:text-sm" data-testid="tab-incomes">Cobranzas</TabsTrigger>
          <TabsTrigger value="payment-types" className="text-xs sm:text-sm" data-testid="tab-payment-types">Valores</TabsTrigger>
          <TabsTrigger value="withholdings" className="text-xs sm:text-sm" data-testid="tab-withholdings">Retenciones</TabsTrigger>
        </TabsList>

        {/* TAB CAJAS/BANCOS */}
        <TabsContent value="cash-banks" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Cajas y Bancos</h2>
            {canCreate && <Button onClick={handleOpenCashBankCreate} data-testid="new-cash-bank-btn"><Plus className="w-4 h-4 mr-2" />Nueva Caja/Banco</Button>}
          </div>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Tipo</TableHead><TableHead>Moneda</TableHead>
                <TableHead>Nro Cuenta / CBU</TableHead>
                <TableHead className="text-center">Estado</TableHead><TableHead className="text-center">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {cashBanks.map(cb => (
                  <TableRow key={cb.cash_bank_id}>
                    <TableCell className="font-medium"><div className="flex items-center gap-2">{getEntityTypeIcon(cb.entity_type)}{cb.name}</div></TableCell>
                    <TableCell>{getEntityTypeName(cb.entity_type)}</TableCell>
                    <TableCell><Badge variant="outline">{cb.currency}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{cb.account_number || cb.cbu_alias || '-'}</TableCell>
                    <TableCell className="text-center"><Switch checked={cb.is_active} onCheckedChange={() => handleToggleCashBankActive(cb)} disabled={!canEdit} /></TableCell>
                    <TableCell><div className="flex justify-center gap-2">
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => handleOpenCashBankEdit(cb)}><Pencil className="w-4 h-4" /></Button>}
                      {canDelete && <Button variant="ghost" size="icon" onClick={() => handleDeleteCashBank(cb)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
                {cashBanks.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No hay cajas o bancos registrados</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* TAB GASTOS */}
        <TabsContent value="expenses" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Conceptos de Gastos (Egresos)</h2>
            {canCreate && <Button onClick={handleOpenExpenseConceptCreate} data-testid="new-expense-concept-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Concepto</Button>}
          </div>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Cuenta Contable</TableHead><TableHead>Comportamiento</TableHead>
                <TableHead className="text-center">Estado</TableHead><TableHead className="text-center">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {[...expenseConcepts].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })).map(ec => (
                  <TableRow key={ec.concept_id}>
                    <TableCell className="font-medium"><div className="flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-red-500" />{ec.name}</div></TableCell>
                    <TableCell>{renderMappedAccount('expense_concept', ec.concept_id)}</TableCell>
                    <TableCell><Badge variant={ec.behavior === 'debit' ? 'default' : 'secondary'}>{ec.behavior === 'debit' ? 'Debe' : 'Haber'}</Badge></TableCell>
                    <TableCell className="text-center"><Switch checked={ec.is_active} onCheckedChange={() => handleToggleExpenseConceptActive(ec)} disabled={!canEdit} /></TableCell>
                    <TableCell><div className="flex justify-center gap-2">
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => handleOpenExpenseConceptEdit(ec)}><Pencil className="w-4 h-4" /></Button>}
                      {canDelete && <Button variant="ghost" size="icon" onClick={() => handleDeleteExpenseConcept(ec)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
                {expenseConcepts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No hay conceptos de gastos</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* TAB COBRANZAS */}
        <TabsContent value="incomes" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Conceptos de Cobranzas (Ingresos)</h2>
            {canCreate && <Button onClick={handleOpenIncomeConceptCreate} data-testid="new-income-concept-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Concepto</Button>}
          </div>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Cuenta Contable</TableHead><TableHead>Comportamiento</TableHead>
                <TableHead className="text-center">Estado</TableHead><TableHead className="text-center">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {incomeConcepts.map(ic => (
                  <TableRow key={ic.concept_id}>
                    <TableCell className="font-medium"><div className="flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-green-500" />{ic.name}</div></TableCell>
                    <TableCell>{renderMappedAccount('income_concept', ic.concept_id)}</TableCell>
                    <TableCell><Badge variant={ic.behavior === 'credit' ? 'default' : 'secondary'}>{ic.behavior === 'credit' ? 'Haber' : 'Debe'}</Badge></TableCell>
                    <TableCell className="text-center"><Switch checked={ic.is_active} onCheckedChange={() => handleToggleIncomeConceptActive(ic)} disabled={!canEdit} /></TableCell>
                    <TableCell><div className="flex justify-center gap-2">
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => handleOpenIncomeConceptEdit(ic)}><Pencil className="w-4 h-4" /></Button>}
                      {canDelete && <Button variant="ghost" size="icon" onClick={() => handleDeleteIncomeConcept(ic)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
                {incomeConcepts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No hay conceptos de cobranzas</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* TAB VALORES */}
        <TabsContent value="payment-types" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Tipos de Valores (Medios de Pago/Cobro)</h2>
            {canCreate && <Button onClick={handleOpenPaymentTypeCreate} data-testid="new-payment-type-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Tipo</Button>}
          </div>
          <Card><CardContent className="pt-6">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nombre</TableHead><TableHead>Categoria</TableHead><TableHead className="text-center">Requiere Referencia</TableHead>
                <TableHead className="text-center">Estado</TableHead><TableHead className="text-center">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {paymentTypes.map(pt => (
                  <TableRow key={pt.payment_type_id}>
                    <TableCell className="font-medium"><div className="flex items-center gap-2">{getCategoryIcon(pt.category)}{pt.name}</div></TableCell>
                    <TableCell><Badge variant="outline">{getCategoryName(pt.category)}</Badge></TableCell>
                    <TableCell className="text-center">{pt.requires_reference ? <Badge className="bg-blue-100 text-blue-800">Si</Badge> : <span className="text-muted-foreground">No</span>}</TableCell>
                    <TableCell className="text-center"><Switch checked={pt.is_active} onCheckedChange={() => handleTogglePaymentTypeActive(pt)} disabled={!canEdit} /></TableCell>
                    <TableCell><div className="flex justify-center gap-2">
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => handleOpenPaymentTypeEdit(pt)}><Pencil className="w-4 h-4" /></Button>}
                      {canDelete && <Button variant="ghost" size="icon" onClick={() => handleDeletePaymentType(pt)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
                {paymentTypes.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No hay tipos de valores</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* TAB RETENCIONES / PERCEPCIONES */}
        <TabsContent value="withholdings" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Retenciones y Percepciones</h2>
              <p className="text-sm text-muted-foreground">Catálogo usado en recibos de OS (retenciones) y comprobantes de compra (percepciones). La cuenta contable se asigna en <b>Plan de Cuentas → Mapeos</b>.</p>
            </div>
            {canCreate && <Button onClick={handleOpenWithholdingCreate} data-testid="new-withholding-btn"><Plus className="w-4 h-4 mr-2" />Nueva Retención/Percepción</Button>}
          </div>
          <Card><CardContent className="pt-6">
            <Table data-testid="withholdings-table">
              <TableHeader><TableRow>
                <TableHead>Código</TableHead><TableHead>Nombre</TableHead><TableHead>Cuenta Contable</TableHead>
                <TableHead className="text-center">Estado</TableHead><TableHead className="text-center">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {withholdingTypes.map(w => (
                  <TableRow key={w.withholding_type_id} data-testid={`withholding-row-${w.withholding_type_id}`}>
                    <TableCell className="font-medium"><div className="flex items-center gap-2"><Receipt className="w-4 h-4 text-amber-500" />{w.code}</div></TableCell>
                    <TableCell>{w.name}</TableCell>
                    <TableCell>{renderMappedAccount('withholding_type', w.withholding_type_id)}</TableCell>
                    <TableCell className="text-center">{w.is_active ? <Badge className="bg-green-100 text-green-800">Activa</Badge> : <Badge variant="secondary">Inactiva</Badge>}</TableCell>
                    <TableCell><div className="flex justify-center gap-2">
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => handleOpenWithholdingEdit(w)} data-testid={`withholding-edit-${w.withholding_type_id}`}><Pencil className="w-4 h-4" /></Button>}
                      {canDelete && w.is_active && <Button variant="ghost" size="icon" onClick={() => handleDeleteWithholding(w)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
                {withholdingTypes.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No hay retenciones/percepciones cargadas</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* DIALOGS */}
      <Dialog open={cashBankDialogOpen} onOpenChange={setCashBankDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingCashBank ? 'Editar Caja/Banco' : 'Nueva Caja/Banco'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={cashBankForm.name} onChange={e => setCashBankForm({...cashBankForm, name: e.target.value})} placeholder="Ej: Caja Principal" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tipo *</Label>
                <select value={cashBankForm.entity_type} onChange={e => {
                  const newType = e.target.value;
                  setCashBankForm({
                    ...cashBankForm,
                    entity_type: newType,
                    // v3.36.0 — Al cambiar entity_type, aplicar defaults de categorías permitidas si todavía no se personalizó
                    allowed_payment_categories: cashBankForm.allowed_payment_categories === null
                      ? DEFAULT_CATS_BY_ENTITY_TYPE[newType] || null
                      : cashBankForm.allowed_payment_categories,
                    // Tarjeta de Crédito: default allow_negative=true (saldo representa deuda)
                    allow_negative: newType === 'credit_card' ? true : cashBankForm.allow_negative,
                  });
                }} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="cash">Caja Fisica</option><option value="bank">Cuenta Bancaria</option><option value="wallet">Billetera Virtual</option><option value="credit_card">Tarjeta de Credito</option>
                </select></div>
              <div><Label>Moneda *</Label>
                <select value={cashBankForm.currency} onChange={e => setCashBankForm({...cashBankForm, currency: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="ARS">ARS</option><option value="USD">USD</option><option value="EUR">EUR</option>
                </select></div>
            </div>
            {cashBankForm.entity_type === 'bank' && (<>
              <div><Label>Numero de Cuenta</Label><Input value={cashBankForm.account_number} onChange={e => setCashBankForm({...cashBankForm, account_number: e.target.value})} /></div>
              <div><Label>CBU / Alias</Label><Input value={cashBankForm.cbu_alias} onChange={e => setCashBankForm({...cashBankForm, cbu_alias: e.target.value})} /></div>
            </>)}
            {!editingCashBank && (<div className="grid grid-cols-2 gap-4">
              <div><Label>Saldo Inicial</Label><MoneyInput value={cashBankForm.initial_balance} onChange={e => setCashBankForm({...cashBankForm, initial_balance: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>Fecha Saldo Inicial</Label><Input type="date" value={cashBankForm.initial_balance_date} onChange={e => setCashBankForm({...cashBankForm, initial_balance_date: e.target.value})} data-testid="initial-balance-date" /></div>
            </div>)}
            {editingCashBank && (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3 space-y-3">
                <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                  <strong>Saldo Inicial de la ficha</strong> — corrige el importe/fecha del movimiento de apertura.
                  Se rehace el ajuste automático (con concepto <em>"Saldo Inicial"</em>) y se recalculan los saldos.
                  Las conciliaciones bancarias no dependen de este campo (usan la suma de todos los movimientos previos al período).
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Saldo Inicial</Label><MoneyInput value={cashBankForm.initial_balance} onChange={e => setCashBankForm({...cashBankForm, initial_balance: parseFloat(e.target.value) || 0})} data-testid="edit-initial-balance" /></div>
                  <div><Label>Fecha Saldo Inicial</Label><Input type="date" value={cashBankForm.initial_balance_date} onChange={e => setCashBankForm({...cashBankForm, initial_balance_date: e.target.value})} data-testid="edit-initial-balance-date" /></div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2"><Switch id="allow_negative" checked={cashBankForm.allow_negative} onCheckedChange={checked => setCashBankForm({...cashBankForm, allow_negative: checked})} /><Label htmlFor="allow_negative">Permitir saldo negativo</Label></div>
            {/* v3.36.0 — Categorías de tipos de valor permitidas en esta caja/banco */}
            <div className="rounded-md border p-3 space-y-2 bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Tipos de valor permitidos en OPs y Cobranzas</Label>
                {cashBankForm.allowed_payment_categories === null && (
                  <span className="text-[10px] text-amber-700">Sin restricciones (permite todas)</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'cash',     label: 'Efectivo' },
                  { key: 'transfer', label: 'Transferencia / Débito automático / Tarjeta Débito' },
                  { key: 'check',    label: 'Cheque / e.Cheq' },
                  { key: 'card',     label: 'Tarjeta de Crédito' },
                ].map(opt => {
                  const current = cashBankForm.allowed_payment_categories;
                  const isChecked = current !== null && current.includes(opt.key);
                  return (
                    <label key={opt.key} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`cat-${opt.key}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          const curr = cashBankForm.allowed_payment_categories ?? [];
                          const next = e.target.checked
                            ? Array.from(new Set([...curr, opt.key]))
                            : curr.filter(c => c !== opt.key);
                          setCashBankForm({ ...cashBankForm, allowed_payment_categories: next });
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
              {Array.isArray(cashBankForm.allowed_payment_categories) && cashBankForm.allowed_payment_categories.length === 0 && (
                <p className="text-[11px] text-rose-700">⚠️ Sin categorías marcadas → esta caja/banco NO se podrá usar para cargar OPs ni Cobranzas.</p>
              )}
              {cashBankForm.allowed_payment_categories !== null && (
                <button type="button"
                  onClick={() => setCashBankForm({...cashBankForm, allowed_payment_categories: null})}
                  className="text-[11px] text-slate-600 underline">
                  Quitar restricciones (permitir todas)
                </button>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCashBankDialogOpen(false)}>Cancelar</Button><Button onClick={handleSaveCashBank}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={expenseConceptDialogOpen} onOpenChange={setExpenseConceptDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingExpenseConcept ? 'Editar Concepto de Gasto' : 'Nuevo Concepto de Gasto'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={expenseConceptForm.name} onChange={e => setExpenseConceptForm({...expenseConceptForm, name: e.target.value})} placeholder="Ej: Alquiler, Servicios" /></div>
            <div>
              <Label>Cuenta Contable</Label>
              <div className="mt-1 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 min-h-10">
                {editingExpenseConcept ? renderMappedAccount('expense_concept', editingExpenseConcept.concept_id) : <span className="text-xs text-muted-foreground">Se asigna desde Plan de Cuentas → Mapeos</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">La imputación contable se gestiona en el Plan de Cuentas (Mapeos), no acá.</p>
            </div>
            <div><Label>Comportamiento</Label>
              <select value={expenseConceptForm.behavior} onChange={e => setExpenseConceptForm({...expenseConceptForm, behavior: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="debit">Debe</option><option value="credit">Haber</option>
              </select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setExpenseConceptDialogOpen(false)}>Cancelar</Button><Button onClick={handleSaveExpenseConcept}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={incomeConceptDialogOpen} onOpenChange={setIncomeConceptDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingIncomeConcept ? 'Editar Concepto de Cobranza' : 'Nuevo Concepto de Cobranza'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={incomeConceptForm.name} onChange={e => setIncomeConceptForm({...incomeConceptForm, name: e.target.value})} placeholder="Ej: Ventas, Servicios" /></div>
            <div>
              <Label>Cuenta Contable</Label>
              <div className="mt-1 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 min-h-10">
                {editingIncomeConcept ? renderMappedAccount('income_concept', editingIncomeConcept.concept_id) : <span className="text-xs text-muted-foreground">Se asigna desde Plan de Cuentas → Mapeos</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">La imputación contable se gestiona en el Plan de Cuentas (Mapeos), no acá.</p>
            </div>
            <div><Label>Comportamiento</Label>
              <select value={incomeConceptForm.behavior} onChange={e => setIncomeConceptForm({...incomeConceptForm, behavior: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="credit">Haber</option><option value="debit">Debe</option>
              </select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIncomeConceptDialogOpen(false)}>Cancelar</Button><Button onClick={handleSaveIncomeConcept}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentTypeDialogOpen} onOpenChange={setPaymentTypeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingPaymentType ? 'Editar Tipo de Valor' : 'Nuevo Tipo de Valor'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={paymentTypeForm.name} onChange={e => setPaymentTypeForm({...paymentTypeForm, name: e.target.value})} placeholder="Ej: Efectivo, Transferencia" /></div>
            <div><Label>Categoria *</Label>
              <select value={paymentTypeForm.category} onChange={e => setPaymentTypeForm({...paymentTypeForm, category: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="cash">Efectivo</option><option value="transfer">Transferencia</option><option value="check">Cheque</option><option value="card">Tarjeta</option>
              </select></div>
            <div className="flex items-center gap-2"><Switch id="requires_reference" checked={paymentTypeForm.requires_reference} onCheckedChange={checked => setPaymentTypeForm({...paymentTypeForm, requires_reference: checked})} /><Label htmlFor="requires_reference">Requiere numero de referencia</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPaymentTypeDialogOpen(false)}>Cancelar</Button><Button onClick={handleSavePaymentType}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={withholdingDialogOpen} onOpenChange={setWithholdingDialogOpen}>
        <DialogContent className="max-w-md" data-testid="withholding-dialog">
          <DialogHeader><DialogTitle>{editingWithholding ? 'Editar Retención/Percepción' : 'Nueva Retención/Percepción'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Código *</Label><Input value={withholdingForm.code} onChange={e => setWithholdingForm({...withholdingForm, code: e.target.value})} placeholder="Ej: IIBB, IVA, GAN, SUSS" data-testid="withholding-code-input" /></div>
            <div><Label>Nombre *</Label><Input value={withholdingForm.name} onChange={e => setWithholdingForm({...withholdingForm, name: e.target.value})} placeholder="Ej: Ingresos Brutos PBA" data-testid="withholding-name-input" /></div>
            <div>
              <Label>Cuenta Contable</Label>
              <div className="mt-1 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 min-h-10">
                {editingWithholding ? renderMappedAccount('withholding_type', editingWithholding.withholding_type_id) : <span className="text-xs text-muted-foreground">Se asigna desde Plan de Cuentas → Mapeos</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">La imputación contable se gestiona en el Plan de Cuentas (Mapeos), no acá.</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setWithholdingDialogOpen(false)}>Cancelar</Button><Button onClick={handleSaveWithholding} data-testid="withholding-save-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TreasuryConfig;
